import { waitBeforeRetryingConnection } from "./wait-before-retrying-connection";

/** Loads a code-split module once and shares it; a failed download is tried again by the next caller. */
export function createModuleLoader<LoadedModule>(
  importModule: () => Promise<LoadedModule>
): () => Promise<LoadedModule> {
  let modulePromise: Promise<LoadedModule> | null = null;
  return () => {
    if (!modulePromise) {
      const pendingModule = importModule();
      modulePromise = pendingModule;
      // A failed download is tried again by the next caller.
      pendingModule.catch(() => {
        if (modulePromise === pendingModule) modulePromise = null;
      });
    }
    return modulePromise;
  };
}

/** Retains existing callers through download failures with one shared reconnect/retry loop. */
export function createRetryingModuleLoader<LoadedModule>(importModule: () => Promise<LoadedModule>) {
  const loadModule = createModuleLoader(importModule);
  const retryDownload = createModuleLoader(async () => {
    for (;;) {
      await waitBeforeRetryingConnection(30000);
      try {
        return await loadModule();
      } catch {
        // Retry after the next connection event or delay without needing another caller.
      }
    }
  });
  return () => loadModule().catch(retryDownload);
}
